import React from "react";
import "../styles.scss";
import { Text } from "@amzn/stencil-react-components/text";
import { Row } from "@amzn/stencil-react-components/layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { StatusContainer } from "./status-container";
import { SdaInfo } from "../../../utility/application-data";
import { JobScheduleDetails } from "../../../utility/application-data";
import {
  IconCalendarFill,
  IconClockFill,
} from "@amzn/stencil-react-components/icons";
import { processTime } from "../../../utility/time-processor";
import useArb from "../../../hooks/use-arb";
import { Link } from "@amzn/stencil-react-components/link";
import ICalendarLink from "react-icalendar-link";
import moment from "moment";

interface StartDateHeaderProps {
  sdaDetails: SdaInfo;
  jcDetails: JobScheduleDetails;
  onViewDetailsClick: () => void;
}

export const FirstDayHeader = ({
  sdaDetails,
  jcDetails,
  onViewDetailsClick,
}: StartDateHeaderProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const startTime = sdaDetails.startTime;
  const processedStartTime = processTime(startTime);
  const startDateAndStartTime =
    jcDetails.firstDayOnSiteL10N?.toString() + " " + processedStartTime;

  const plainStartDateAndStartTime = new Date(startDateAndStartTime);
  const timeFormat = "YYYY-MM-DD hh:mm A";
  const dateTzWithStartTime = moment.tz(
    startDateAndStartTime,
    timeFormat,
    sdaDetails.timezone
  );
  const currDate = new Date();
  const currentDateTime = moment.tz(currDate, sdaDetails.timezone);
  const starttimeDifference = dateTzWithStartTime.diff(currentDateTime, "day");

  const dateTranslated = bundle.formatMessage(
    "Checklist-AppointmentCard-Month-Day-Year",
    {
      dateParam: plainStartDateAndStartTime,
    }
  );

  const timeTranslated = bundle.formatMessage(
    "Checklist-AppointmentCard-Time",
    {
      dateParam: plainStartDateAndStartTime,
    }
  );

  const countdownMsg1 = bundle.formatMessage(
    "Checklist-AppointmentCard-StartDayMessage"
  );
  const countdownMsg2 = bundle.formatMessage(
    "Checklist-AppointmentCard-First-Day-Message"
  );
  const addToCalendar = bundle.formatMessage(
    "Checklist-AppointmentCard-ButtonText3"
  );

  const viewDetails = bundle.formatMessage(
    "Checklist-AppointmentCard-ButtonText1"
  );
  const appointmentTitle = bundle.formatMessage(
    "Checklist-CardControlled-FirstDayTitle"
  );

  const googlePlaceURL = "http://maps.google.com/?q=";
  const firstDayCalendarEvent = {
    title: appointmentTitle,
    startTime: dateTzWithStartTime.format(),
    location: sdaDetails.address,
  };

  const largeViewImage =
    starttimeDifference > 0
      ? require("./star.svg").default
      : require("./backpack.svg").default;
  const smallViewPortImage =
    starttimeDifference > 0
      ? require("./star.svg").default
      : require("./backpack.svg").default;
  const headerText =
    starttimeDifference > 0 ? (
      <Text fontSize={"T400"} textAlign="center">
        {starttimeDifference + " " + countdownMsg1}
      </Text>
    ) : (
      <Text fontSize={"T400"} textAlign="center">
        {countdownMsg2}
      </Text>
    );

  return (
    <StatusContainer
      largeViewPortImage={largeViewImage}
      smallViewPortImage={smallViewPortImage}
    >
      {headerText}

      <Row gridGap={"1rem"}>
        <Row gridGap={"0.3rem"} alignItems={"center"}>
          <IconCalendarFill />
          <Text fontSize={"T200"}>{dateTranslated}</Text>
        </Row>
        <Row gridGap={"0.3rem"} alignItems={"center"}>
          <IconClockFill />
          <Text fontSize={"T200"}>
            {timeTranslated +
              " " +
              moment().tz(sdaDetails.timezone).zoneAbbr().substring(-2)}
          </Text>
        </Row>
      </Row>

      <Link
        fontSize={"T200"}
        href={googlePlaceURL + sdaDetails.address}
        target={"_blank"}
        className={"link-style"}
      >
        {sdaDetails.address}
      </Link>
      <Row gridGap={"1rem"}>
        <ICalendarLink
          key="calendar-button"
          filename={appointmentTitle}
          event={firstDayCalendarEvent}
          className={"add-to-calendar"}
        >
          <Button size={ButtonSize.Small}>{addToCalendar}</Button>
        </ICalendarLink>
        <Button
          variant={ButtonVariant.Primary}
          size={ButtonSize.Small}
          onClick={onViewDetailsClick}
        >
          {viewDetails}
        </Button>
      </Row>
    </StatusContainer>
  );
};
